import { useEffect, useState, type FormEvent } from 'react'
import type { GameListing, RoomSummary } from '@v2/multiplayer'
import { normalizeRoomCode } from '@v2/multiplayer'
import { Icon } from '@v2/react'
import { api, errorMessage } from './network'
import { saveName, savedName } from './identity'
import { Page, Wordmark, timeAgo } from './page'
import { Credits } from './Credits'

/** The front door: a name, a new table, or a code. Nothing else competes for attention. */
export function Home() {
  const [name, setName] = useState(savedName)
  const [games, setGames] = useState<GameListing[]>([])
  const [gameId, setGameId] = useState<string>('')
  const [rooms, setRooms] = useState<RoomSummary[] | null>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState<'create' | 'join' | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => { document.title = 'Tabletop with Friends' }, [])
  useEffect(() => {
    let cancelled = false
    void api.games().then(list => { if (cancelled) return; setGames(list); setGameId(current => current || list[0]?.id || '') }).catch(() => {})
    void api.myRooms().then(list => { if (!cancelled) setRooms(list) }).catch(() => { if (!cancelled) setRooms([]) })
    return () => { cancelled = true }
  }, [])
  const ready = name.trim().length > 0
  const create = async (event: FormEvent) => {
    event.preventDefault()
    if (!ready || busy) return
    setBusy('create'); setError(null); saveName(name)
    try { const created = await api.createRoom(name.trim(), gameId || undefined); location.assign(`/play/${created.roomId}`) }
    catch (cause) { setError(errorMessage(cause)); setBusy(null) }
  }
  const join = async (event: FormEvent) => {
    event.preventDefault()
    const normalized = normalizeRoomCode(code)
    if (!normalized) { setError('Codes are six letters or digits.'); return }
    if (busy) return
    setBusy('join'); setError(null); if (ready) saveName(name)
    try { const found = await api.resolveCode(normalized); location.assign(`/play/${found.roomId}`) }
    catch (cause) { setError(errorMessage(cause)); setBusy(null) }
  }
  return <Page>
    <header className="pg-masthead"><Wordmark/><p>Board games with friends, in the browser.</p></header>
    {error && <p className="pg-error" role="alert">{error}</p>}
    <form className="pg-card" onSubmit={create} aria-label="Start a table">
      <label className="pg-field"><span>Your name</span><input className="tt-input" value={name} onChange={event => setName(event.target.value)} maxLength={40} autoComplete="nickname" placeholder="What should we call you?" required/></label>
      {games.length > 1 && <fieldset className="pg-choices"><legend>Game</legend>{games.map(game => <label key={game.id} className={`pg-choice${gameId === game.id ? ' is-selected' : ''}`}><input type="radio" name="game" value={game.id} checked={gameId === game.id} onChange={() => setGameId(game.id)}/><span>{game.name}</span><small>{game.players.min === game.players.max ? `${game.players.max} players` : `${game.players.min}–${game.players.max} players`}</small></label>)}</fieldset>}
      <button className="tt-primary-button pg-wide" disabled={!ready || busy !== null}>{busy === 'create' ? 'Setting the table…' : 'Start a table'}<Icon name="arrow" size={14}/></button>
    </form>
    <form className="pg-row" onSubmit={join} aria-label="Join with a code">
      <label className="pg-field pg-grow"><span>Have a code?</span><input className="tt-input pg-code" value={code} onChange={event => setCode(event.target.value.toUpperCase())} maxLength={7} autoCapitalize="characters" autoComplete="off" spellCheck={false} placeholder="ABC123" aria-label="Table code"/></label>
      <button className="tt-secondary-button" disabled={busy !== null}>{busy === 'join' ? 'Finding…' : 'Join'}</button>
    </form>
    {rooms && rooms.length > 0 && <section className="pg-section" aria-label="Your tables">
      <h2>Your tables</h2>
      <ul className="pg-list">{rooms.map(room => <li key={room.roomId}><a className="pg-room" href={`/play/${room.roomId}`}>
        <span className={`pg-dot is-${room.phase}`} aria-hidden="true"/>
        <span className="pg-room-main"><strong>{room.gameName ?? 'Choosing a game'}</strong><small>{room.leaderName}’s table · {room.memberCount} {room.memberCount === 1 ? 'player' : 'players'} · {room.phase === 'playing' ? 'in progress' : room.phase === 'complete' ? 'finished' : 'in the lobby'}</small></span>
        <span className="pg-room-side"><code>{room.code}</code><small>{timeAgo(room.lastActiveAt)}</small></span>
      </a></li>)}</ul>
    </section>}
    <Credits/>
  </Page>
}
